import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { Calendar, Users, Activity, Globe, Download } from 'lucide-react';
import { SiteData } from '../../types';
import * as XLSX from 'xlsx';

interface SiteDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: string;
  sites: SiteData[];
  isHealthy: boolean;
}

export const SiteDetailsModal: React.FC<SiteDetailsModalProps> = ({
  isOpen,
  onClose,
  location,
  sites,
  isHealthy,
}) => {
  // Separate truly active sites (online AND >=3 active users) from just online sites
  const trulyActiveSites = sites.filter(site => site.isOnline && site.activeUsers >= 3);
  const onlineButLowActivitySites = sites.filter(site => site.isOnline && site.activeUsers < 3);
  const offlineSites = sites.filter(site => !site.isOnline);
  
  const handleExportExcel = () => {
    // Prepare data for Excel export
    const exportData = sites.map(site => ({
      'Site Name': site.siteName,
      'Customer': site.customer,
      'Country': site.country,
      'Region': site.region,
      'Status': site.isOnline ? 'Online' : 'Offline',
      'Site Type': site.siteType,
      'ML Version': site.mlVersion,
      'DB Version': site.dbVersion,
      'Install Date': site.siteInstallDate,
      'License Expiration': site.siteLicenseExpirationInML,
      'Start Usage': site.startUsage,
      'Last Usage': site.lastUsage,
      'Months In Use': site.monthsInUse,
      'Used Last Month': site.usedLastMonth ? 'Yes' : 'No',
      'Simulators Connected': site.simulatorsConnected,
      'Registered Users': site.registeredUsers,
      'Active Users': site.activeUsers,
      'Inactive Users': site.inactiveUsers,
      'Sessions Last Month': site.numSessionsLastMonth,
      'Sessions on Server': site.numSessionsOnServer,
      'Sessions Last 12 Months': site.numSessionsOnServerLast12Month,
      'Active Users (12 Months)': site.activeUsers12Months
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, `${location} Sites`);
    
    // Save file
    const fileName = `${location.replace(/\s+/g, '_')}_Sites_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const totalSessions = sites.reduce((sum, site) => sum + (site.numSessionsOnServerLast12Month || 0), 0);
  const totalUsers = sites.reduce((sum, site) => sum + (site.activeUsers || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] animate-scale-in">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <DialogTitle className="text-xl">
                {location} Sites
              </DialogTitle>
              <Badge variant={isHealthy ? "default" : "destructive"} className="ml-2">
                {isHealthy ? 'Healthy' : 'Needs Attention'}
              </Badge>
            </div>
            <Button
              onClick={handleExportExcel}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </Button>
          </div>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-card border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Truly Active</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{trulyActiveSites.length}</div>
              <div className="text-xs text-muted-foreground">Online + {'>'}10 sessions</div>
            </div>
            <div className="bg-card border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium">Low Activity</span>
              </div>
              <div className="text-2xl font-bold text-yellow-600">{onlineButLowActivitySites.length}</div>
              <div className="text-xs text-muted-foreground">Online but ≤10 sessions</div>
            </div>
            <div className="bg-card border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium">Offline</span>
              </div>
              <div className="text-2xl font-bold text-red-600">{offlineSites.length}</div>
              <div className="text-xs text-muted-foreground">Not Connected</div>
            </div>
            <div className="bg-card border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Total Sessions</span>
              </div>
              <div className="text-2xl font-bold">{totalSessions.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Last 12 months</div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] mt-4">
          <div className="space-y-3">
            {/* Truly Active Sites Section */}
            {trulyActiveSites.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <h3 className="font-semibold text-green-700">Truly Active Sites ({trulyActiveSites.length})</h3>
                  <Badge className="bg-green-100 text-green-800 text-xs">Online + {'>'}10 Sessions</Badge>
                </div>
                {trulyActiveSites.map((site, index) => (
                  <div key={`truly-active-${index}`} className="border border-green-200 rounded-lg p-4 bg-green-50/50 hover-scale">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-foreground">{site.siteName}</h4>
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            {site.customer}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">ML Version:</span>
                            <div className="font-medium">{site.mlVersion}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Sessions (12M):</span>
                            <div className="font-medium text-green-600">{site.numSessionsOnServerLast12Month?.toLocaleString() || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Active Users:</span>
                            <div className="font-medium">{site.activeUsers || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Simulators:</span>
                            <div className="font-medium">{site.simulatorsConnected || 0}</div>
                          </div>
                        </div>
                        {site.siteLicenseExpirationInML && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            License expires: {new Date(site.siteLicenseExpirationInML).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Low Activity Sites Section */}
            {onlineButLowActivitySites.length > 0 && (
              <>
                {trulyActiveSites.length > 0 && <Separator className="my-4" />}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <h3 className="font-semibold text-yellow-700">Low Activity Sites ({onlineButLowActivitySites.length})</h3>
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">Online but ≤10 Sessions</Badge>
                </div>
                {onlineButLowActivitySites.map((site, index) => (
                  <div key={`low-activity-${index}`} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50/50 hover-scale">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-foreground">{site.siteName}</h4>
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            {site.customer}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">ML Version:</span>
                            <div className="font-medium">{site.mlVersion}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Sessions (12M):</span>
                            <div className="font-medium text-yellow-600">{site.numSessionsOnServerLast12Month?.toLocaleString() || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Registered Users:</span>
                            <div className="font-medium">{site.registeredUsers || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Last Usage:</span>
                            <div className="font-medium">{site.lastUsage ? new Date(site.lastUsage).toLocaleDateString() : 'N/A'}</div>
                          </div>
                        </div>
                        {site.siteLicenseExpirationInML && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            License expires: {new Date(site.siteLicenseExpirationInML).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Separator */}
            {(trulyActiveSites.length > 0 || onlineButLowActivitySites.length > 0) && offlineSites.length > 0 && (
              <Separator className="my-4" />
            )}

            {/* Offline Sites Section */}
            {offlineSites.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <h3 className="font-semibold text-red-700">Offline Sites ({offlineSites.length})</h3>
                  <Badge variant="destructive" className="text-xs">Not Connected</Badge>
                </div>
                {offlineSites.map((site, index) => (
                  <div key={`offline-${index}`} className="border border-red-200 rounded-lg p-4 bg-red-50/50 hover-scale">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-foreground">{site.siteName}</h4>
                          <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
                            {site.customer}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">ML Version:</span>
                            <div className="font-medium">{site.mlVersion}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Last Sessions:</span>
                            <div className="font-medium text-red-600">{site.numSessionsOnServerLast12Month?.toLocaleString() || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Registered Users:</span>
                            <div className="font-medium">{site.registeredUsers || 0}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Install Date:</span>
                            <div className="font-medium">{site.siteInstallDate ? new Date(site.siteInstallDate).toLocaleDateString() : 'N/A'}</div>
                          </div>
                        </div>
                        {site.siteLicenseExpirationInML && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            License expires: {new Date(site.siteLicenseExpirationInML).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};